import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import {
  sortOrgStandardVersions,
  type OrgStandard,
  type OrgStandardVersion
} from '../models/orgStandards';

export type SettingsOrgStandardsStatus =
  | 'loading'
  | 'ready'
  | 'unauth'
  | 'empty'
  | 'error';

export type ViewerRoleSystemKey = 'owner' | 'admin' | 'member' | null;

export interface UseSettingsOrgStandardsVM {
  status: SettingsOrgStandardsStatus;
  /** Active (home) org id. Null until the membership load resolves. */
  orgId: string | null;
  orgName: string | null;
  /** Viewer's permission_role.system_key in the active org. */
  viewerRole: ViewerRoleSystemKey;
  /** Owner / Admin — author the standards. Members read-only (Edit tab
   *  becomes a rendered-markdown panel; no Save / Archive / History controls). */
  canManage: boolean;
  /** The org's singleton standards row. `id` is null when no row exists yet
   *  (the org has never authored standards). */
  standard: OrgStandard | null;
  /** Working copy of the standards content, or null when clean. */
  draft: string | null;
  /** The value the Edit textarea should render — draft when dirty, else the
   *  saved content. */
  editorValue: string;
  /** True when `draft` differs from the saved content. */
  isDirty: boolean;
  /** Top-level load error message (shown via a Banner). */
  error: string | null;
  /** `org_standard_versions` for the org's standards row, newest first. Empty
   *  when no row exists yet. Consumed by the History tab. */
  selectedVersions: OrgStandardVersion[];
  /** Author display handles by user id — for the History tab `By` column. */
  handles: Record<string, string>;
  /** True while a Save RPC is in flight. */
  saving: boolean;
  /** True while an archive/restore RPC is in flight. */
  archiving: boolean;
  /** Version id whose restore RPC is in flight, or null. */
  restoringVersionId: string | null;
  /** Save failure message (shown via a Banner). Cleared on the next edit. */
  saveError: string | null;
  /** Transient "Saved." confirmation (shown via a Toast). */
  saveToast: string | null;
  /** Archive/restore failure message (shown via a Banner). */
  archiveError: string | null;
  /** Version-restore failure message (shown via a Banner in the History tab). */
  restoreError: string | null;
  /** Transient restore confirmation (shown via a Toast). "Restored." on a
   *  successful restore; "Already at this version." on an `already_at_version`
   *  no-op. */
  restoreToast: string | null;
  actions: {
    /** Patch the working copy. Seeds from saved content on first edit. */
    editDraft: (value: string) => void;
    /** Discard unsaved edits, reverting to the saved content. */
    cancelEdit: () => void;
    /** Seed a starter example into the working copy (appends to any existing
     *  content). Marks the editor dirty. */
    insertExample: (text: string) => void;
    /** Persist the working copy via `save_org_standard`. Optimistically updates
     *  content + `lastPropagatedAt`; reverts on failure (the working copy is
     *  restored so edits aren't lost). No-op when clean. */
    save: () => Promise<void>;
    /** Archive the standards via `set_org_standard_archived` (optimistic
     *  `archivedAt` toggle, reverts on failure). No-op when there is no row. */
    archive: () => Promise<void>;
    /** Restore (un-archive) the standards via `set_org_standard_archived`. */
    restore: () => Promise<void>;
    /** Restore the standards to a prior version via
     *  `restore_org_standard_version`. Refreshes content + versions on success;
     *  an `already_at_version` no-op surfaces an "Already at this version."
     *  toast. */
    restoreVersion: (versionId: string) => Promise<void>;
    /** Dismiss the "Saved." toast. */
    clearSaveToast: () => void;
    /** Dismiss the restore toast. */
    clearRestoreToast: () => void;
  };
}

interface OrgStandardRow {
  id: string;
  org_id: string;
  content: string;
  last_propagated_at: string | null;
  archived_at: string | null;
}

interface OrgStandardVersionRow {
  id: string;
  org_standard_id: string;
  version_number: number;
  content: string;
  restored_from_version_id: string | null;
  created_at: string;
  created_by: string | null;
}

function toVersion(row: OrgStandardVersionRow): OrgStandardVersion {
  return {
    id: row.id,
    orgStandardId: row.org_standard_id,
    versionNumber: row.version_number,
    content: row.content,
    restoredFromVersionId: row.restored_from_version_id,
    createdAt: row.created_at,
    createdBy: row.created_by
  };
}

/** The `org_standards` row shape `save_org_standard` /
 *  `set_org_standard_archived` / `restore_org_standard_version` return inside
 *  their `{ok:true, standard}` envelope (`to_jsonb` of the full row — only
 *  these fields are consumed). */
interface OrgStandardEnvelopeRow {
  id: string;
  org_id: string;
  content: string;
  last_propagated_at: string | null;
  archived_at: string | null;
}

interface ProfileRow {
  id: string;
  display_name: string | null;
  username: string | null;
}

/**
 * ViewModel for `/settings/org-standards` (turn 10.2a — read-only single-pane
 * view shell + Edit-tab working copy).
 *
 * Resolves the caller's home org (id + name) and viewer
 * permission_role.system_key with the same "oldest membership wins" heuristic
 * as the sibling settings VMs, then loads the org's singleton `org_standards`
 * row. When no row exists the VM still resolves `ready` with a `standard` whose
 * `id` is null and whose `content` is the empty string — the org has never
 * authored standards.
 *
 * Readable by Members too — there is no `forbidden` status. The View gates Save
 * / Archive / History affordances on `canManage`. Those mutations + the live
 * Preview tab land in turns 10.2b–10.2e; this turn ships the working-copy draft
 * + dirty tracking only — no Save RPC.
 */
export function useSettingsOrgStandardsVM(): UseSettingsOrgStandardsVM {
  const [status, setStatus] = useState<SettingsOrgStandardsStatus>('loading');
  // The active org is owned by the shell (see `useActiveOrg`). This VM is a
  // pure consumer: it scopes every query to whatever org the shell says is
  // active and re-loads when the user switches. It must NOT re-derive its own
  // "oldest membership" org — that was the cross-org leak. The public shape
  // still exposes these as `orgId` / `orgName`, now sourced from context.
  const { activeOrgId: orgId, activeOrgName: orgName } = useActiveOrg();
  const [viewerRole, setViewerRole] = useState<ViewerRoleSystemKey>(null);
  const [standard, setStandard] = useState<OrgStandard | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<OrgStandardVersion[]>([]);
  const [handles, setHandles] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(
    null
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreToast, setRestoreToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!orgId) return;

      // Viewer's permission_role.system_key for THIS org's membership.
      const { data: memRow, error: memErr } = await supabase
        .from('memberships')
        .select('permission_role:permission_role_id(system_key)')
        .eq('user_id', session.user.id)
        .eq('org_id', orgId)
        .maybeSingle();

      if (cancelled) return;
      if (memErr) {
        setStatus('error');
        setError("Couldn't load org standards. Try again.");
        return;
      }

      type RoleEmbed =
        | { system_key?: string | null }
        | { system_key?: string | null }[]
        | null;
      const membership = memRow as { permission_role: RoleEmbed } | null;
      const sysKey = membership
        ? Array.isArray(membership.permission_role)
          ? membership.permission_role[0]?.system_key ?? null
          : membership.permission_role?.system_key ?? null
        : null;
      const role: ViewerRoleSystemKey =
        sysKey === 'owner' || sysKey === 'admin' || sysKey === 'member'
          ? sysKey
          : null;

      // Load the org's singleton org_standards row + its version history in
      // parallel. maybeSingle — a brand-new org has no row yet.
      const [stdRes, versionsRes] = await Promise.all([
        supabase
          .from('org_standards')
          .select('id, org_id, content, last_propagated_at, archived_at')
          .eq('org_id', orgId)
          .maybeSingle(),
        supabase
          .from('org_standard_versions')
          .select(
            'id, org_standard_id, version_number, content, restored_from_version_id, created_at, created_by'
          )
          .eq('org_id', orgId)
          .order('version_number', { ascending: false })
      ]);

      if (cancelled) return;
      if (stdRes.error || versionsRes.error) {
        setStatus('error');
        setError("Couldn't load org standards. Try again.");
        return;
      }

      const row = stdRes.data as OrgStandardRow | null;
      const versionRows = (versionsRes.data ??
        []) as ReadonlyArray<OrgStandardVersionRow>;
      const resolved: OrgStandard = row
        ? {
            id: row.id,
            orgId: row.org_id,
            content: row.content,
            lastPropagatedAt: row.last_propagated_at,
            archivedAt: row.archived_at
          }
        : {
            id: null,
            orgId,
            content: '',
            lastPropagatedAt: null,
            archivedAt: null
          };

      // Resolve author display handles for the version rows (History `By`).
      const authorIds = Array.from(
        new Set(
          versionRows
            .map((v) => v.created_by)
            .filter((id): id is string => !!id)
        )
      );
      const handleMap: Record<string, string> = {};
      if (authorIds.length > 0) {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', authorIds);
        if (cancelled) return;
        for (const p of (profileRows ?? []) as ReadonlyArray<ProfileRow>) {
          handleMap[p.id] = p.display_name || p.username || 'someone';
        }
      }

      setViewerRole(role);
      setStandard(resolved);
      setVersions(versionRows.map(toVersion));
      setHandles(handleMap);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const isDirty = useMemo(() => {
    if (draft === null || !standard) return false;
    return draft !== standard.content;
  }, [draft, standard]);

  const editDraft = useCallback((value: string) => {
    setDraft(value);
    setSaveError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setDraft(null);
    setSaveError(null);
  }, []);

  const insertExample = useCallback(
    (text: string) => {
      setDraft((cur) => {
        const base = cur ?? standard?.content ?? '';
        return base.trim() === '' ? text : `${base}\n${text}`;
      });
    },
    [standard]
  );

  const clearSaveToast = useCallback(() => setSaveToast(null), []);
  const clearRestoreToast = useCallback(() => setRestoreToast(null), []);

  /** Re-fetch `org_standard_versions` for the org after a write so the History
   *  tab stays current, then resolve any newly-seen author handles.
   *  Best-effort — a failure leaves the prior list. */
  const reloadVersions = useCallback(async () => {
    if (!orgId) return;
    const { data, error: vErr } = await supabase
      .from('org_standard_versions')
      .select(
        'id, org_standard_id, version_number, content, restored_from_version_id, created_at, created_by'
      )
      .eq('org_id', orgId)
      .order('version_number', { ascending: false });
    if (vErr || !data) return;
    const rows = data as ReadonlyArray<OrgStandardVersionRow>;
    setVersions(rows.map(toVersion));

    // Resolve handles for any authors not already cached.
    const seen = Array.from(
      new Set(
        rows.map((v) => v.created_by).filter((id): id is string => !!id)
      )
    );
    setHandles((cur) => {
      const missing = seen.filter((id) => !(id in cur));
      if (missing.length === 0) return cur;
      void (async () => {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', missing);
        if (!profileRows) return;
        setHandles((prev) => {
          const next = { ...prev };
          for (const p of profileRows as ReadonlyArray<ProfileRow>) {
            next[p.id] = p.display_name || p.username || 'someone';
          }
          return next;
        });
      })();
      return cur;
    });
  }, [orgId]);

  const save = useCallback(async () => {
    if (!standard || draft === null || draft === standard.content) return;
    const content = draft;
    setSaveError(null);
    setSaving(true);

    // Optimistic — apply the working copy + a fresh propagated timestamp.
    const snapshot = standard;
    setStandard({
      ...standard,
      content,
      lastPropagatedAt: new Date().toISOString()
    });
    setDraft(null);

    const { data, error: rpcError } = await supabase.rpc('save_org_standard', {
      p_org_id: standard.orgId,
      p_content: content
    });
    setSaving(false);

    const envelope = data as
      | { ok: true; standard: OrgStandardEnvelopeRow }
      | { ok: false; error: string }
      | null;
    if (rpcError || !envelope || envelope.ok !== true) {
      // Revert — and restore the working copy so the edit isn't lost.
      setStandard(snapshot);
      setDraft(content);
      setSaveError("Couldn't save the standards. Try again.");
      return;
    }

    // Reconcile with the authoritative row the RPC returned.
    const row = envelope.standard;
    setStandard({
      id: row.id,
      orgId: row.org_id,
      content: row.content,
      lastPropagatedAt: row.last_propagated_at,
      archivedAt: row.archived_at
    });
    setSaveToast('Saved.');
    void reloadVersions();
  }, [standard, draft, reloadVersions]);

  const setArchived = useCallback(
    async (archived: boolean) => {
      if (!standard || !standard.id) return;
      if (archived === (standard.archivedAt !== null)) return;
      const stdId = standard.id;
      setArchiveError(null);
      setArchiving(true);

      // Optimistic archivedAt toggle.
      const snapshot = standard;
      setStandard({
        ...standard,
        archivedAt: archived ? new Date().toISOString() : null
      });

      const { data, error: rpcError } = await supabase.rpc(
        'set_org_standard_archived',
        { p_org_standard_id: stdId, p_archived: archived }
      );
      setArchiving(false);

      const envelope = data as
        | { ok: true; standard: OrgStandardEnvelopeRow }
        | { ok: false; error: string }
        | null;
      if (rpcError || !envelope || envelope.ok !== true) {
        setStandard(snapshot);
        setArchiveError(
          archived
            ? "Couldn't archive the standards. Try again."
            : "Couldn't restore the standards. Try again."
        );
        return;
      }

      // Reconcile with the authoritative row.
      const row = envelope.standard;
      setStandard({
        id: row.id,
        orgId: row.org_id,
        content: row.content,
        lastPropagatedAt: row.last_propagated_at,
        archivedAt: row.archived_at
      });
      void reloadVersions();
    },
    [standard, reloadVersions]
  );

  const archive = useCallback(() => setArchived(true), [setArchived]);
  const restore = useCallback(() => setArchived(false), [setArchived]);

  const restoreVersion = useCallback(
    async (versionId: string) => {
      if (!standard) return;
      setRestoreError(null);
      setRestoringVersionId(versionId);

      const { data, error: rpcError } = await supabase.rpc(
        'restore_org_standard_version',
        { p_version_id: versionId }
      );
      setRestoringVersionId(null);

      const envelope = data as
        | { ok: true; standard: OrgStandardEnvelopeRow }
        | { ok: false; error: string }
        | null;
      if (rpcError || !envelope) {
        setRestoreError("Couldn't restore the version. Try again.");
        return;
      }
      if (envelope.ok !== true) {
        if (envelope.error === 'already_at_version') {
          setRestoreToast('Already at this version.');
          return;
        }
        setRestoreError("Couldn't restore the version. Try again.");
        return;
      }

      // Reconcile the live row with the restored content + drop any working
      // copy so the Edit tab shows the restored text.
      const row = envelope.standard;
      setStandard({
        id: row.id,
        orgId: row.org_id,
        content: row.content,
        lastPropagatedAt: row.last_propagated_at,
        archivedAt: row.archived_at
      });
      setDraft(null);
      setRestoreToast('Restored.');
      void reloadVersions();
    },
    [standard, reloadVersions]
  );

  const editorValue = draft ?? standard?.content ?? '';
  const canManage = viewerRole === 'owner' || viewerRole === 'admin';

  const selectedVersions = useMemo(() => {
    if (!standard?.id) return [];
    const stdId = standard.id;
    return sortOrgStandardVersions(
      versions.filter((v) => v.orgStandardId === stdId)
    );
  }, [versions, standard]);

  return {
    status,
    orgId,
    orgName,
    viewerRole,
    canManage,
    standard,
    draft,
    editorValue,
    isDirty,
    error,
    selectedVersions,
    handles,
    saving,
    archiving,
    restoringVersionId,
    saveError,
    saveToast,
    archiveError,
    restoreError,
    restoreToast,
    actions: {
      editDraft,
      cancelEdit,
      insertExample,
      save,
      archive,
      restore,
      restoreVersion,
      clearSaveToast,
      clearRestoreToast
    }
  };
}
